import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, CalendarClock, Play, Users } from "lucide-react";

export default function TournamentGames() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [homeTeamId, setHomeTeamId] = useState("");
  const [awayTeamId, setAwayTeamId] = useState("");
  const [starting, setStarting] = useState(false);

  const teams = Array.isArray(tournament?.teams) ? tournament.teams : [];
  const homeTeam = teams.find((team) => team.id === homeTeamId) || null;
  const awayTeam = teams.find((team) => team.id === awayTeamId) || null;

  const rosterPreview = useMemo(() => {
    const rows = [];
    for (const team of [homeTeam, awayTeam]) {
      if (!team) continue;
      (team.players || []).forEach((player) => {
        rows.push({
          id: player.id,
          teamId: team.id,
          teamName: team.name,
          name: player.name || "Unnamed",
          email: player.email || "",
          phone: player.phone || "",
          position: player.position || "",
          location: player.location || "",
        });
      });
    }
    return rows;
  }, [homeTeam, awayTeam]);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    Promise.all([
      api.get(`/tournaments/${tournamentId}`),
      api.get(`/tournaments/${tournamentId}/games`),
    ])
      .then(([tournamentRes, gamesRes]) => {
        if (!alive) return;
        setTournament(tournamentRes.data || null);
        setGames(Array.isArray(gamesRes.data) ? gamesRes.data : []);
      })
      .catch((err) => {
        if (!alive) return;
        console.warn("[tournament-games] load failed", err);
        toast.error(err?.response?.data?.detail || "Unable to load tournament games");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [tournamentId]);

  const goToModeSelection = () => {
    if (!homeTeamId || !awayTeamId) {
      toast.error("Select both teams before continuing");
      return;
    }
    if (homeTeamId === awayTeamId) {
      toast.error("Choose two different teams");
      return;
    }

    setStarting(true);
    navigate(`/tournaments/${tournamentId}/games/mode`, {
      state: { homeTeamId, awayTeamId },
    });
    setStarting(false);
  };

  if (loading) {
    return <div className="max-w-6xl mx-auto px-6 py-12 text-zinc-400">Loading game workflow…</div>;
  }

  if (!tournament) {
    return <div className="max-w-6xl mx-auto px-6 py-12 text-zinc-400">Unable to load tournament details.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-12 sm:px-6 space-y-8">
      <div className="flex items-center justify-between gap-4">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-court">Tournament Games</div>
      </div>

      <div className="border border-zinc-800 p-6">
        <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-court">{tournament.name}</div>
        <h1 className="font-display font-black text-4xl uppercase mt-2">Game Record Center</h1>
        <p className="text-sm text-zinc-400 mt-2">Manage prior game sessions and open a fresh stat-recording contest for this tournament.</p>
      </div>

      <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-6">
        <section className="border border-zinc-800 p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-court">Previous Games</div>
              <div className="text-sm text-zinc-400 mt-1">Previously recorded contests for this tournament.</div>
            </div>
            <div className="text-xs text-zinc-500 font-mono">{games.length} total</div>
          </div>

          {!games.length ? (
            <div className="border border-dashed border-zinc-800 p-8 text-sm text-zinc-500 text-center">No previous games have been started for this tournament yet.</div>
          ) : (
            <div className="space-y-3">
              {games.map((game) => (
                <Link
                  key={game.id}
                  to={game.status === "scheduled" ? `/tournaments/${tournamentId}/games/${game.id}/mode` : `/tournaments/${tournamentId}/games/${game.id}`}
                  className="block border border-zinc-800 p-4 hover:border-court transition-colors"
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-display font-black text-xl uppercase text-white">{game.home_team_name} vs {game.away_team_name}</div>
                      <div className="text-xs text-zinc-500 mt-1 font-mono">{game.created_at ? new Date(game.created_at).toLocaleString() : "New game"}</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-court">{game.status || "ready"}</div>
                  </div>
                  <div className="text-sm text-zinc-400 mt-3">{game.roster_count || 0} players preloaded for recording.</div>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="border border-zinc-800 p-6 space-y-4">
          <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-court">Start a New Game</div>

          <div className="grid gap-3">
            <label className="block">
              <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">Home Team</span>
              <select value={homeTeamId} onChange={(e) => setHomeTeamId(e.target.value)} className="w-full mt-2 bg-zinc-950 border border-zinc-800 focus:border-court py-2.5 px-3 outline-none text-white">
                <option value="">Choose team</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">Away Team</span>
              <select value={awayTeamId} onChange={(e) => setAwayTeamId(e.target.value)} className="w-full mt-2 bg-zinc-950 border border-zinc-800 focus:border-court py-2.5 px-3 outline-none text-white">
                <option value="">Choose team</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </label>
          </div>

          {rosterPreview.length > 0 && (
            <div className="border border-court/40 bg-court/10 p-3">
              <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-court">Auto-filled players</div>
              <div className="mt-3 grid gap-2">
                {rosterPreview.map((player) => (
                  <div key={`${player.teamId}-${player.id}`} className="border border-zinc-800 bg-zinc-950/50 p-3">
                    <div className="font-display font-black text-lg uppercase text-white">{player.name}</div>
                    <div className="text-xs text-zinc-400">{player.teamName}</div>
                    <div className="mt-2 text-xs text-zinc-400 flex flex-wrap gap-2">
                      {player.email && <span>{player.email}</span>}
                      {player.phone && <span>{player.phone}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button onClick={goToModeSelection} disabled={starting || !homeTeamId || !awayTeamId || !rosterPreview.length} className="w-full h-12 px-5 bg-court text-white font-display font-black tracking-wider uppercase border-b-4 border-court-deep hover:bg-court-hover active:translate-y-0.5 active:border-b-0 transition-all disabled:opacity-50">
            <span className="inline-flex items-center gap-2"><Play className="w-4 h-4" /> {starting ? "Continuing…" : "Continue to Match Setup"}</span>
          </button>

          {!teams.length && (
            <div className="text-sm text-zinc-500">Add at least two teams to this tournament before starting a game.</div>
          )}
        </section>
      </div>

      <div className="flex items-center gap-2">
        <Link to={`/tournaments/${tournamentId}`} className="inline-flex items-center gap-2 border border-zinc-800 px-4 py-2 text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-white">
          <Users className="w-4 h-4" /> Back to tournament
        </Link>
      </div>
    </div>
  );
}
